import { type SubmitFunction } from '@remix-run/react'

import { type UseFormResetField } from 'react-hook-form'
import { NEXT } from '#app/constants/forms.ts'

import { checkIfCurrentStepHasChildren } from '#app/features/form/wizard/utils/checkIfCurrentStepHasChildren.ts'

import { handleSubmitForm } from '#app/features/form/wizard/utils/handleSubmitForm.ts'

import {
	type BackendActions,
	type Action,
	type JSONState,
	type QuestionText,
} from '#types/form/wizard.ts'
import { type GlobalFormStore } from '../reducer/createGlobalFormStore.ts'
import { checkIfValuesHaveChanged } from './checkIfValuesHaveChanged.ts'
import { removeChildrenTrees } from './removeChildrenTrees.ts'

interface CurrentAnswer {
	[key: string]: string
}

interface HandleStepLogic<T> {
	questionTree: JSONState<T>['questionTree']
	formState: JSONState<T>['formState']
	oldFormState: JSONState<T>['oldFormState']
	dispatch: React.Dispatch<Action<T>>
	submit: SubmitFunction
	resetField: UseFormResetField<any>
	fieldKey: string
	backendAction: BackendActions
	CSRFToken: string
	actionRoute: string
	id: string
	questionText: QuestionText[]
	hasBackendCall: boolean
	currentFormContext: CurrentAnswer
	wizardMessages: JSONState<T>['wizardMessages']
	entireGlobalState: GlobalFormStore<T>
	answeredQuestionsStack: JSONState<T>['answeredQuestionsStack']
}

export function handleStepLogic<T>({
	questionTree,
	formState,
	oldFormState,
	dispatch,
	submit,
	actionRoute,
	resetField,
	CSRFToken,
	backendAction,
	fieldKey,
	hasBackendCall,
	id,
	questionText,
	currentFormContext,
	entireGlobalState,
	wizardMessages,
	answeredQuestionsStack,
}: HandleStepLogic<T>) {
	const { currentScores } = entireGlobalState as JSONState<T>

	if (hasBackendCall) {
		const { hasChildren, whichChildren } = checkIfCurrentStepHasChildren({
			questionTree,
			id,
		})

		const valuesHaveNotChanged = checkIfValuesHaveChanged({
			formState,
			oldFormState,
			id,
		})

		const alreadyHasHighRating = currentScores!.some(item => {
			if (item.id === id) {
				if (item.score > 8) {
					return true
				}
			}

			return false
		})

		if (!hasChildren) {
			if (valuesHaveNotChanged && alreadyHasHighRating) {
				dispatch({
					type: NEXT,
					formData: currentFormContext as Partial<T>,
					fieldKey,
				})
			} else {
				handleSubmitForm({
					fieldKey,
					backendAction,
					id,
					actionRoute,
					CSRFToken,
					questionText,
					currentFormContext,
					wizardMessages,
					entireGlobalState,
					submit,
				})
			}
		}

		if (hasChildren) {
			if (valuesHaveNotChanged) {
				dispatch({
					type: NEXT,
					formData: currentFormContext as Partial<T>,
					fieldKey,
				})
			} else {
				const [
					filteredTrees,
					newFormState,
					filteredMessages,
					filteredAnsweredQuestionsStack,
				] = removeChildrenTrees({
					questionTree,
					formState,
					wizardMessages,
					whichChildren: whichChildren!,
					currentId: id,
					answeredQuestionsStack,
					resetField,
				})

				const newState = {
					...entireGlobalState,
					questionTree: filteredTrees,
					formState: newFormState,
					wizardMessages: filteredMessages,
					answeredQuestionsStack: filteredAnsweredQuestionsStack,
				}

				handleSubmitForm({
					fieldKey,
					backendAction,
					id,
					actionRoute,
					questionText,
					CSRFToken,
					currentFormContext,
					wizardMessages: filteredMessages,
					entireGlobalState: newState,
					submit,
				})
			}
		}
	}
}
